import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, AlertTriangle, CheckCircle2, CreditCard, Gift } from 'lucide-react';
import { useSubscription, PLAN_TIER, PLAN_NAMES } from '../context/SubscriptionContext';
import { cancelSubscription, redeemVip } from '../api/services';

const Account = () => {
  const { sub, refresh, loading } = useSubscription();
  const nav = useNavigate();
  const [cancelling, setCancelling] = React.useState(false);
  const [vipCode, setVipCode] = React.useState('');
  const [redeeming, setRedeeming] = React.useState(false);
  const [vipResult, setVipResult] = React.useState(null);

  const doCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will keep access until the end of your current billing period.')) return;
    setCancelling(true);
    try {
      await cancelSubscription();
      await refresh();
    } catch (e) {
      alert('Could not cancel. Please contact support.');
    } finally { setCancelling(false); }
  };

  const doRedeem = async (e) => {
    e?.preventDefault?.();
    const code = vipCode.trim().toUpperCase();
    if (!code) return;
    setRedeeming(true);
    setVipResult(null);
    try {
      const res = await redeemVip(code);
      setVipResult({ ok: true, ...res });
      await refresh();
      setVipCode('');
    } catch (err) {
      setVipResult({ ok: false, error: err?.response?.data?.detail || 'Invalid code' });
    } finally {
      setRedeeming(false);
    }
  };

  const periodEnd = sub?.period_end ? new Date(sub.period_end).toLocaleDateString() : null;
  const isActive = sub?.status === 'active';
  const isCanceled = sub?.status === 'canceled';

  return (
    <div className="min-h-screen px-8 py-8 max-w-3xl mx-auto">
      <button onClick={() => nav(-1)} className="text-white/60 hover:text-white text-sm">← Back</button>
      <h1 className="font-display text-5xl text-white mt-2">Account</h1>
      <p className="text-white/60">Manage your plan and billing.</p>

      <div className="mt-8 p-8 rounded-2xl bg-white/5 border border-white/10">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-white/60 text-sm uppercase tracking-widest">Current plan</div>
            <div className="flex items-center gap-3 mt-2">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${PLAN_TIER[sub?.plan] === 'live' ? 'bg-amber-500/20 text-amber-300' : PLAN_TIER[sub?.plan] === 'basic' ? 'bg-[#E50914]/20 text-[#E50914]' : 'bg-white/10 text-white/60'}`}>
                <Crown size={20} />
              </div>
              <div>
                <div className="text-2xl font-semibold text-white" data-testid="account-plan-name">{PLAN_NAMES[sub?.plan] || sub?.plan || 'Free'}</div>
                <div className="text-white/50 text-sm">{sub?.interval === 'trial' ? '7-day free trial' : sub?.interval === 'annual' ? 'Annual plan' : sub?.interval === 'monthly' ? 'Monthly plan' : sub?.status === 'active' ? 'Active plan' : 'No active plan'}</div>
              </div>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
            isActive ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40' :
            isCanceled ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' :
            'bg-white/10 text-white/60 border border-white/15'
          }`}>
            {loading ? '…' : (sub?.status || 'inactive').toUpperCase()}
          </div>
        </div>

        {isActive && periodEnd && (
          <div className="mt-5 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex gap-3">
            <CheckCircle2 className="text-emerald-400 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-emerald-200">
              {sub?.interval === 'trial'
                ? <>Your free trial ends on <span className="font-semibold">{periodEnd}</span>. Subscribe to Basic for $2.99/mo to keep watching.</>
                : <>Active until <span className="font-semibold">{periodEnd}</span>. You’ll be charged again on this date unless you cancel.</>}
            </div>
          </div>
        )}
        {isCanceled && periodEnd && (
          <div className="mt-5 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex gap-3">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-amber-200">
              Canceled. Your plan remains active until <span className="font-semibold">{periodEnd}</span>. After that you’ll be on the Free tier.
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          {!isActive ? (
            <Link to="/subscribe" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#f6121d] text-white font-semibold">
              <CreditCard size={16} /> Subscribe
            </Link>
          ) : (
            <>
              <Link to="/subscribe" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white">
                Change plan
              </Link>
              {!isCanceled && (
                <button onClick={doCancel} disabled={cancelling}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-red-300 hover:text-red-200 border border-red-500/30 disabled:opacity-60">
                  {cancelling ? 'Canceling…' : 'Cancel subscription'}
                </button>
              )}
            </>
          )}
        </div>

        <div className="mt-8 text-xs text-white/40">
          Payments are processed securely by Square. We never store your card details. For billing questions, contact <a href="mailto:ponomobiz@gmail.com" className="text-white/70 hover:text-white">ponomobiz@gmail.com</a>.
        </div>
      </div>

      {/* VIP redemption */}
      <div className="mt-6 p-8 rounded-2xl bg-gradient-to-br from-amber-500/10 via-white/5 to-transparent border border-amber-500/30">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500/20 text-amber-300 flex items-center justify-center">
            <Gift size={20} />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white">Have a VIP code?</h3>
            <p className="text-white/60 text-sm">Redeem a free-access code for instant plan or PPV access.</p>
          </div>
        </div>

        <form onSubmit={doRedeem} className="mt-5 flex gap-2">
          <input
            value={vipCode}
            onChange={(e) => setVipCode(e.target.value.toUpperCase())}
            placeholder="ENTER CODE"
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/30 uppercase tracking-wider focus:outline-none focus:border-amber-400/60"
          />
          <button
            type="submit"
            disabled={!vipCode.trim() || redeeming}
            className="px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold disabled:opacity-60"
          >
            {redeeming ? 'Redeeming…' : 'Redeem'}
          </button>
        </form>

        {vipResult && (
          vipResult.ok ? (
            <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-2">
              <CheckCircle2 size={16} />
              {vipResult.lifetime ? `Lifetime ${vipResult.plan?.toUpperCase()} access granted!` :
                vipResult.period_end ? `${vipResult.plan?.toUpperCase()} active until ${new Date(vipResult.period_end).toLocaleDateString()}` :
                vipResult.show_id ? 'Show access granted!' : 'Redeemed!'}
            </div>
          ) : (
            <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2">
              <AlertTriangle size={16} /> {vipResult.error}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Account;
